import { t } from '../../services/i18n';

type FaqCategory = {
  id: string;
  questions: string[];
};

export class RcHelpView extends HTMLElement {
  state: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  errorMessage = '';

  private readonly faqCategories: FaqCategory[] = [
    {
      id: 'gettingStarted',
      questions: ['setup', 'import', 'overview', 'countries']
    },
    {
      id: 'propertiesTenants',
      questions: ['propertySetup', 'buildingUnits', 'tenantTracking', 'welcomePage', 'issueReporting', 'placeDescriptions']
    },
    {
      id: 'agreementsDocuments',
      questions: ['agreementInfo', 'documentFollowUp', 'pdfDossier', 'productBoundary']
    },
    {
      id: 'planningPayments',
      questions: ['remindersEvents', 'manualPayments', 'dashboardCalendar', 'recurringWorkflow']
    },
    {
      id: 'dataSupport',
      questions: ['dataQuality', 'newOnboarding', 'timelineUsage', 'profileManagement', 'helpContact']
    }
  ];

  render(): void {
    this.innerHTML = `
      <div class="help-page">
        <section class="help-hero">
          <div class="landing-container">
            <h1 class="help-hero-title">${t('help.hero.title')}</h1>
            <p class="help-hero-sub">${t('help.hero.subtitle')}</p>
          </div>
        </section>
        <section class="help-faq-section">
          <div class="landing-container">
            ${this.renderFaqContent()}
          </div>
        </section>
        <section class="help-contact-section" id="contact-form">
          <div class="landing-container">
            <div class="help-contact-card">
              <h2 class="help-contact-title">${t('help.contact.title')}</h2>
              ${this.renderFormContent()}
            </div>
          </div>
        </section>
      </div>
    `;

    this.querySelector<HTMLFormElement>('#help-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('help-submit', { bubbles: true, detail: { form: event.currentTarget as HTMLFormElement } }));
    });
    this.querySelector('#help-reset-btn')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('help-reset', { bubbles: true })));
  }

  private renderFaqContent(): string {
    return `
      <div class="help-faq-layout">
        <div class="help-faq-header">
          <div class="help-faq-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r=".5" fill="currentColor"/></svg></div>
          <div class="help-faq-header-copy">
            <h2>${t('help.faq.title')}</h2>
            <p>${t('help.faq.intro')}</p>
          </div>
        </div>
        <nav class="help-faq-category-nav" aria-label="${t('help.faq.navLabel')}">
          ${this.faqCategories.map((category) => `
            <a class="help-faq-category-link" href="#faq-${category.id}">
              <span class="help-faq-category-link-title">${t(`help.faq.categories.${category.id}.title`)}</span>
              <span class="help-faq-category-link-meta">${t('help.faq.questionCount', { count: category.questions.length })}</span>
            </a>
          `).join('')}
        </nav>
        <div class="help-faq-groups">
          ${this.faqCategories.map((category) => `
            <section class="help-faq-group" id="faq-${category.id}">
              <div class="help-faq-group-heading">
                <h3>${t(`help.faq.categories.${category.id}.title`)}</h3>
                <p>${t(`help.faq.categories.${category.id}.description`)}</p>
              </div>
              <div class="help-faq-items">
                ${category.questions.map((questionId) => `
                  <details class="help-faq-item">
                    <summary>
                      <span>${t(`help.faq.questions.${questionId}.question`)}</span>
                      <span class="help-faq-caret" aria-hidden="true">v</span>
                    </summary>
                    <div class="help-faq-answer">
                      <p>${t(`help.faq.questions.${questionId}.answer`)}</p>
                    </div>
                  </details>
                `).join('')}
              </div>
            </section>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderFormContent(): string {
    if (this.state === 'success') {
      return `<div class="help-success"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg><h3>${t('help.success.title')}</h3><p>${t('help.success.body')}</p><button class="btn btn-outline" id="help-reset-btn">${t('help.success.reset')}</button></div>`;
    }
    return `
      <form id="help-form" class="help-form" novalidate>
        <div class="form-row">
          <div class="form-group"><label for="help-name">${t('help.form.name')} <span class="required">*</span></label><input id="help-name" name="name" type="text" required autocomplete="name" placeholder="${t('help.form.namePlaceholder')}"></div>
          <div class="form-group"><label for="help-email">${t('help.form.email')} <span class="required">*</span></label><input id="help-email" name="email" type="email" required autocomplete="email" placeholder="${t('help.form.emailPlaceholder')}"></div>
        </div>
        <div class="form-group"><label for="help-subject">${t('help.form.subject')} <span class="required">*</span></label><input id="help-subject" name="subject" type="text" required placeholder="${t('help.form.subjectPlaceholder')}"></div>
        <div class="form-group"><label for="help-message">${t('help.form.message')} <span class="required">*</span></label><textarea id="help-message" name="message" rows="5" required placeholder="${t('help.form.messagePlaceholder')}"></textarea></div>
        ${this.errorMessage ? `<div class="alert alert-danger" role="alert">${this.errorMessage}</div>` : ''}
        <div class="help-form-actions"><button type="submit" class="btn btn-primary${this.state === 'loading' ? ' is-loading' : ''}" id="help-submit-btn" ${this.state === 'loading' ? 'disabled' : ''}>${this.state === 'loading' ? t('help.form.sending') : t('help.form.submit')}</button></div>
      </form>`;
  }
}

if (!customElements.get('rc-help-view')) {
  customElements.define('rc-help-view', RcHelpView);
}
